import { useEffect, useState } from "react";
import { Badge } from "@zweb/ui";
import { apiFetch } from "../lib/api";

interface Document {
  id: string;
  name: string;
  type: string;
  url: string;
  size: number;
  createdAt: string;
}

interface Deployment {
  id: string;
  status: string;
  currentStage: string;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  evaluatedAt: string | null;
  evaluationScore: number | null;
  documents: Document[];
}

const stageLabels: Record<string, string> = {
  document_upload: "Upload de Documentos",
  scheduling: "Agendamento",
  deployment: "Implantação",
  evaluation: "Avaliação",
};

const statusLabels: Record<string, string> = {
  pending: "Pendente",
  documents_pending: "Documentos Pendentes",
  scheduled: "Agendado",
  in_progress: "Em Andamento",
  completed: "Concluído",
  evaluation: "Avaliação",
};

const statusColors: Record<string, string> = {
  pending: "bg-gray-100 text-gray-800",
  documents_pending: "bg-yellow-100 text-yellow-800",
  scheduled: "bg-accent-100 text-accent-800",
  in_progress: "bg-orange-100 text-orange-800",
  completed: "bg-green-100 text-green-800",
  evaluation: "bg-accent-100 text-accent-800",
};

const stages = [
  { key: "document_upload", label: "Documentos" },
  { key: "scheduling", label: "Agendamento" },
  { key: "deployment", label: "Implantação" },
  { key: "evaluation", label: "Avaliação" },
];

export default function DeploymentPage() {
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch("/api/deployment")
      .then((json) => {
        if (json.success) {
          setDeployment(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const getStageStatus = (stageKey: string) => {
    if (!deployment) return "pending";
    const stageOrder = ["document_upload", "scheduling", "deployment", "evaluation"];
    const currentIndex = stageOrder.indexOf(deployment.currentStage);
    const stageIndex = stageOrder.indexOf(stageKey);

    if (stageIndex < currentIndex) return "completed";
    if (stageIndex === currentIndex) return "current";
    return "pending";
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/dashboard" className="text-sm font-medium hover:text-accent-600">Dashboard</a>
            <a href="/chamados" className="text-sm font-medium hover:text-accent-600">Chamados</a>
            <a href="/downloads" className="text-sm font-medium hover:text-accent-600">Downloads</a>
            <a href="/licencas" className="text-sm font-medium hover:text-accent-600">Licenças</a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="container py-8">
        <div className="mx-auto max-w-4xl">
          <h1 className="text-3xl font-bold">Status da Implantação</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Acompanhe o progresso da implantação do ZWeb
          </p>

          {!deployment ? (
            <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
              <p className="text-gray-600 dark:text-gray-400">
                Nenhuma implantação encontrada. Entre em contato com nossa equipe comercial para iniciar o processo.
              </p>
              <a
                href="/solicitar-demo"
                className="mt-4 inline-block rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700"
              >
                Solicitar Demonstração
              </a>
            </div>
          ) : (
            <>
              {/* Status Geral */}
              <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Status Atual</h2>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">
                      Etapa: {stageLabels[deployment.currentStage] || deployment.currentStage}
                    </p>
                  </div>
                  <Badge className={statusColors[deployment.status]}>
                    {statusLabels[deployment.status]}
                  </Badge>
                </div>
              </div>

              {/* Timeline */}
              <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
                <h2 className="text-xl font-semibold">Progresso</h2>
                <div className="mt-6 space-y-4">
                  {stages.map((stage, index) => {
                    const status = getStageStatus(stage.key);
                    return (
                      <div key={stage.key} className="flex items-center gap-4">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                            status === "completed"
                              ? "border-green-500 bg-green-500 text-white"
                              : status === "current"
                              ? "border-accent-500 bg-accent-500 text-white"
                              : "border-gray-300 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800"
                          }`}
                        >
                          {status === "completed" ? (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <span className="text-sm font-medium">{index + 1}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className={`font-medium ${status === "pending" ? "text-gray-400" : ""}`}>
                            {stage.label}
                          </div>
                          {status === "current" && (
                            <div className="text-sm text-accent-600">Etapa atual</div>
                          )}
                        </div>
                        {index < stages.length - 1 && (
                          <div className={`h-px w-8 ${status === "completed" ? "bg-green-500" : "bg-gray-300 dark:bg-gray-700"}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Documentos */}
              {deployment.documents && deployment.documents.length > 0 && (
                <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
                  <h2 className="text-xl font-semibold">Documentos</h2>
                  <div className="mt-4 space-y-3">
                    {deployment.documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-gray-800"
                      >
                        <div className="flex items-center gap-3">
                          <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div>
                            <div className="font-medium">{doc.name}</div>
                            <div className="text-sm text-gray-500">
                              {new Date(doc.createdAt).toLocaleDateString("pt-BR")}
                            </div>
                          </div>
                        </div>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-600 hover:underline"
                        >
                          Baixar
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Datas */}
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                {deployment.scheduledAt && (
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Agendado para</div>
                    <div className="mt-1 text-lg font-semibold">
                      {new Date(deployment.scheduledAt).toLocaleDateString("pt-BR")}
                    </div>
                  </div>
                )}
                {deployment.startedAt && (
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Iniciado em</div>
                    <div className="mt-1 text-lg font-semibold">
                      {new Date(deployment.startedAt).toLocaleDateString("pt-BR")}
                    </div>
                  </div>
                )}
                {deployment.completedAt && (
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Concluído em</div>
                    <div className="mt-1 text-lg font-semibold">
                      {new Date(deployment.completedAt).toLocaleDateString("pt-BR")}
                    </div>
                  </div>
                )}
                {deployment.evaluationScore && (
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Avaliação</div>
                    <div className="mt-1 flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className={`h-5 w-5 ${
                            star <= deployment.evaluationScore! ? "text-yellow-400" : "text-gray-300"
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
